import * as React from 'react';
import styled from 'styled-components';

import { Input } from '../ui/form/Input';
import { H1 } from './HeaderTitle';
import { InputRow } from '../ui/form/InputRow';
import { ButtonGroup } from '../ui/button/ButtonGroup';
import { Button } from '../ui/button/Button';
import { ButtonLink } from '../ui/button/ButtonLine';
import { useForm } from '../../utils/hooks/useForm';
import { useMutation, useQuery } from '@apollo/client';
import { useParams, useRouteMatch } from 'react-router';
import { Textarea } from '../ui/form/TextArea';
import { FormError } from '../ui/form/FormError';
import { PageContentType } from '../../../server/global/enum';

//GraphQL
import { CONTENT_ITEM } from '../../../client/__graphql__/queries/page_content/getPageContent';
import { Query, QueryGetPageContentInputArgs } from '../../../client/types/generated/types';
import { CONTACT_MUTATION } from '../../../client/__graphql__/mutations/contact';

export const SectionContactStyle = styled.div`
  background-color: ${({ theme }) => theme.colors.redwine};
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 4.5rem;
  padding-top: 4em;
  padding-bottom: 3em;
`;

export const Form = styled.form`
  width: 100%;
  padding: 0 1em;

  @media all and (max-width: 450px) {
    margin-top: -2em;
  }
`;

export const FormWrapper = styled.div`
  width: 100%;
  max-width: 36em;

  p {
    @media all and (min-width: 315px) and (max-width: 1100px) {
      padding: 1em 0.6em 1em !important;
      margin-top: -1em;
    }
  }

  .intro {
    margin: 0 0 2em 0;

    p {
      margin-top: 0;
      padding: 0;

      :last-child {
        margin-bottom: 0;
      }
    }
  }
`;

const ContactWrapper = styled.div`
  margin-top: 4em;
  h3 {
    color: ${({ theme }) => theme.colors.primary};
    text-align: center;
    font-size: 27px;
  }
`;

type GetPageItem = Pick<Query, 'getNdPageContent'>;
type GetPageItemVariables = QueryGetPageContentInputArgs;

export const SectionContact: React.FC<{ style?: any }> = ({ style }) => {
  const params = useParams<{ urltag: string }>();
  const match = useRouteMatch();
  const [posted, setPosted] = React.useState(false);
  const contactRef = React.useRef<null | HTMLDivElement>(null);
  const [count_call, SetCountCall] = React.useState(0);
  const [scroll_to_item, SetScrollToItem] = React.useState(false);

  if (!scroll_to_item && match?.url?.trim()?.toLowerCase()?.endsWith('/kontakt')) {
    SetScrollToItem(true);
  }

  const { data: contact, loading: cloading } = useQuery<GetPageItem, GetPageItemVariables>(CONTENT_ITEM, {
    skip: !params.urltag,
    fetchPolicy: 'network-only',
    variables: {
      input: { urltag: params.urltag, type: PageContentType.CONTACT }
    }
  });

  const [lead, { error, loading }] = useMutation(CONTACT_MUTATION, {
    onError: ({ graphQLErrors, networkError }) => {
      console.log(graphQLErrors, networkError);
    },
    onCompleted: ({ lead: { contact } }) => {
      if (contact && contact.success) {
        setPosted(true);
      }
    }
  });

  const ScrollToElement = () => {
    if (contactRef && count_call < 1) {
      SetScrollToItem(false);
      SetCountCall(1);

      setTimeout(() => {
        contactRef?.current?.scrollIntoView();
      }, 200);
    }
  };

  React.useEffect(() => {
    if (scroll_to_item) {
      ScrollToElement();
    }
  }, [scroll_to_item]);

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'name',
        placeholder: 'Ola Nordmann',
        value: '',
        label: 'Navn *',
        required: true
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks.1234',
        value: '',
        label: 'Postnummer',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 12345678',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'textarea',
        name: 'comment',
        placeholder: 'Din beskjed',
        value: '',
        label: 'Kommmentar *',
        required: false
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      lead({
        variables: {
          input: {
            leadType: 'KONTAKT',
            pageType: 'KONTOR',
            identifier: params.urltag,
            name: preparedFields.name,
            zip: preparedFields.zip,
            email: preparedFields.email,
            phone: preparedFields.phone,
            comment: preparedFields.comment,
            referer: window.location.href
          }
        }
      });
    }
  });

  return (
    // Bør væres sjekket på contact.type istedenfor contact.id?
    // Verdien for type står som null i playground, enda den er lagt til
    // som tekst i databasen
    <SectionContactStyle id={'contact'} ref={contactRef} style={style}>
      <FormWrapper>
        {contact?.getNdPageContent?.ingress ? (
          <p
            className={'intro'}
            dangerouslySetInnerHTML={{
              __html: contact?.getNdPageContent?.ingress
            }}
          />
        ) : null}
        {contact?.getNdPageContent?.title ? (
          <H1 style={{ paddingTop: '0em' }}>{contact?.getNdPageContent?.title}</H1>
        ) : (
          <H1 style={{ paddingTop: '2em' }}>Kontakt oss</H1>
        )}
        {contact?.getNdPageContent?.text ? (
          <p
            style={{ margin: '0 1em' }}
            dangerouslySetInnerHTML={{
              __html: contact?.getNdPageContent?.text
            }}
          />
        ) : null}
        {!posted ? (
          <Form onSubmit={handleSubmit} style={{ paddingTop: '2em' }}>
            {error?.message ? <FormError message={error?.message} /> : null}
            {fields.map((input, index) => {
              if (input?.type === 'textarea') {
                return (
                  <InputRow key={`field_${index}`}>
                    <Textarea
                      name={input?.name}
                      label={input?.label}
                      value={input?.value}
                      onChange={handleChange}
                      error={input?.error}
                      placeholder={input?.placeholder}
                    />
                  </InputRow>
                );
              }
              return (
                <InputRow key={`field_${index}`}>
                  <Input
                    name={input?.name}
                    type={input?.type}
                    label={input?.label}
                    value={input?.value}
                    onChange={handleChange}
                    error={input?.error}
                    placeholder={input?.placeholder}
                  />
                </InputRow>
              );
            })}
            <ButtonGroup>
              <Button submit type="primary" colorTheme="gold">
                Kontakt meg
              </Button>
              <ButtonLink href="https://privatmegleren.no/personvern" as="a" className="privacy-link-mobile ">
                {' '}
                Personvern
              </ButtonLink>
            </ButtonGroup>
          </Form>
        ) : (
          <ContactWrapper>
            <h3>Takk for din henvendelse!</h3>
          </ContactWrapper>
        )}
      </FormWrapper>
    </SectionContactStyle>
  );
};
